import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase } from '@material-ui/core';
import tokens from '../theme/tokens';

// 관리자 화면 공용 부품 — 카드·필드·입력·토글·버튼·표·배지·빈 상태. 사이트 토큰(주황·둥근 모서리)만 쓴다
const useStyles = makeStyles(() => ({
  card: {
    padding: '22px 24px',
    borderRadius: 16,
    backgroundColor: tokens.surface,
    boxShadow: '0 4px 16px rgba(22, 22, 23, 0.06)',
    '& + &': { marginTop: 16 },
  },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    '& h3': { margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: -0.8, color: tokens.ink },
    '& p': { margin: '4px 0 0', fontSize: 13, color: tokens.muted, letterSpacing: -0.4 },
  },
  field: {
    display: 'block',
    marginBottom: 14,
    '& > span': { display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 700, color: tokens.text, letterSpacing: -0.4 },
  },
  input: {
    width: '100%',
    height: 44,
    padding: '0 14px',
    borderRadius: tokens.radius,
    border: `1px solid ${tokens.line}`,
    backgroundColor: tokens.surface,
    fontSize: 14,
    fontFamily: 'inherit',
    letterSpacing: -0.4,
    color: tokens.ink,
    outline: 'none',
    transition: 'border-color .12s, box-shadow .12s',
    '&:focus': { borderColor: tokens.primary, boxShadow: tokens.shadow },
    '&::placeholder': { color: tokens.placeholder },
    '&:disabled': { backgroundColor: tokens.surfaceSoft, color: tokens.muted },
  },
  textarea: {
    height: 'auto',
    minHeight: 96,
    padding: '12px 14px',
    resize: 'vertical',
    lineHeight: 1.5,
  },
  select: {
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27><path fill=%27none%27 stroke=%27%23a4a4ac%27 stroke-width=%272%27 stroke-linecap=%27round%27 d=%27M7 10l5 5 5-5%27/></svg>")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: 16,
    paddingRight: 36,
  },
  toggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: 14,
    color: tokens.text,
    letterSpacing: -0.4,
  },
  track: {
    position: 'relative',
    width: 40,
    height: 22,
    borderRadius: 11,
    backgroundColor: tokens.line,
    transition: 'background-color .15s',
    '&:after': {
      content: '""',
      position: 'absolute',
      top: 2,
      left: 2,
      width: 18,
      height: 18,
      borderRadius: 9,
      backgroundColor: tokens.surface,
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      transition: 'left .15s',
    },
  },
  trackOn: {
    backgroundColor: tokens.primary,
    '&:after': { left: 20 },
  },
  btn: {
    height: 40,
    padding: '0 16px',
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: -0.4,
    transition: 'filter .12s, background-color .12s',
    '&:hover': { filter: 'brightness(0.97)' },
    '&:disabled': { opacity: 0.5 },
  },
  primary: { backgroundColor: tokens.primary, color: tokens.surface, '&:hover': { backgroundColor: tokens.primaryDark } },
  line: { border: `1px solid ${tokens.primary}`, backgroundColor: tokens.surface, color: tokens.primaryDark, '&:hover': { backgroundColor: tokens.primarySoft } },
  ghost: { backgroundColor: tokens.surfaceSoft, color: tokens.text, '&:hover': { backgroundColor: tokens.lineSoft } },
  danger: { border: `1px solid ${tokens.line}`, backgroundColor: tokens.surface, color: '#d64545', '&:hover': { backgroundColor: '#fff3f3', borderColor: '#f3b4b4' } },
  sm: { height: 32, padding: '0 12px', fontSize: 13, borderRadius: 16 },
  tableWrap: { width: '100%', overflowX: 'auto' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
    letterSpacing: -0.4,
    '& th': { padding: '10px 10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: tokens.muted, borderBottom: `1px solid ${tokens.lineSoft}`, whiteSpace: 'nowrap' },
    '& td': { padding: '12px 10px', color: tokens.ink, borderBottom: `1px solid ${tokens.lineSoft}`, verticalAlign: 'middle' },
    '& tr:last-child td': { borderBottom: 'none' },
    '& tbody tr:hover td': { backgroundColor: '#fffaf8' },
  },
  badge: {
    display: 'inline-block',
    height: 22,
    padding: '0 8px',
    borderRadius: 11,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: '22px',
    letterSpacing: -0.4,
    backgroundColor: tokens.surfaceSoft,
    color: tokens.text,
    whiteSpace: 'nowrap',
  },
  badgeOn: { backgroundColor: tokens.primarySoft, color: tokens.primaryDark },
  badgeOff: { backgroundColor: tokens.surfaceSoft, color: tokens.muted },
  badgeDark: { backgroundColor: tokens.ink, color: tokens.surface },
  empty: {
    padding: '36px 0',
    textAlign: 'center',
    fontSize: 14,
    color: tokens.muted,
    letterSpacing: -0.4,
  },
  row: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 14 },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', columnGap: 14 },
  pager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 14,
    fontSize: 13,
    color: tokens.muted,
  },
  msg: {
    marginTop: 10,
    fontSize: 13,
    letterSpacing: -0.4,
    color: tokens.primaryDark,
  },
  ok: { color: '#2f9e5f' },
}));

export function Card({ title, desc, action, children, className }) {
  const cs = useStyles();
  return (
    <section className={clsx(cs.card, className)}>
      {(title || action) && (
        <div className={cs.cardHead}>
          <div>{title && <h3>{title}</h3>}{desc && <p>{desc}</p>}</div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Field({ label, children, className }) {
  const cs = useStyles();
  return <label className={clsx(cs.field, className)}><span>{label}</span>{children}</label>;
}

export function Input({ className, ...props }) {
  const cs = useStyles();
  return <input className={clsx(cs.input, className)} {...props} />;
}

export function Textarea({ className, ...props }) {
  const cs = useStyles();
  return <textarea className={clsx(cs.input, cs.textarea, className)} {...props} />;
}

export function Select({ className, children, ...props }) {
  const cs = useStyles();
  return <select className={clsx(cs.input, cs.select, className)} {...props}>{children}</select>;
}

export function Toggle({ checked, onChange, label }) {
  const cs = useStyles();
  return (
    <span className={cs.toggle} role="switch" aria-checked={checked} tabIndex={0} onClick={() => onChange(!checked)} onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onChange(!checked); } }}>
      <span className={clsx(cs.track, checked && cs.trackOn)} />
      {label && <span>{label}</span>}
    </span>
  );
}

export function Btn({ tone = 'primary', size, className, children, ...props }) {
  const cs = useStyles();
  return <ButtonBase className={clsx(cs.btn, cs[tone], size === 'sm' && cs.sm, className)} focusRipple={false} {...props}>{children}</ButtonBase>;
}

export function Table({ head, children }) {
  const cs = useStyles();
  return (
    <div className={cs.tableWrap}>
      <table className={cs.table}>
        <thead><tr>{head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Badge({ tone = 'default', children }) {
  const cs = useStyles();
  return <span className={clsx(cs.badge, tone === 'on' && cs.badgeOn, tone === 'off' && cs.badgeOff, tone === 'dark' && cs.badgeDark)}>{children}</span>;
}

export function Empty({ children }) {
  const cs = useStyles();
  return <div className={cs.empty}>{children}</div>;
}

export function Row({ children, className }) {
  const cs = useStyles();
  return <div className={clsx(cs.row, className)}>{children}</div>;
}

export function Grid({ cols = 2, children, className }) {
  const cs = useStyles();
  return <div className={clsx(cols === 3 ? cs.grid3 : cs.grid2, className)}>{children}</div>;
}

export function Pager({ page, limit, total, onChange }) {
  const cs = useStyles();
  const pages = Math.max(1, Math.ceil((total || 0) / (limit || 20)));
  return (
    <div className={cs.pager}>
      <span>{total || 0}건 · {page}/{pages}</span>
      <Btn tone="ghost" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>이전</Btn>
      <Btn tone="ghost" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>다음</Btn>
    </div>
  );
}

export function Msg({ ok, children }) {
  const cs = useStyles();
  if (!children) return null;
  return <div className={clsx(cs.msg, ok && cs.ok)}>{children}</div>;
}

export const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '-';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
export const fmtWon = (n) => `${Number(n || 0).toLocaleString('ko-KR')}원`;
export const toLocalInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
